import React, { useMemo } from 'react';
import { BORDER_RADIUS_SMALL, TYPOGRAPHY } from '@/styles';
import { createStyles } from '@/contexts/theme-context';
import { EqualHeightTouchableOpacity } from '@/components/equal-height-touchable-opacity';
import { OrderableItemListItemWithRowQuantity } from './orderable-item-list-item';
import { SCROLL_DELAY } from '@/components/list-item';
import { StyleProp, View, ViewStyle } from 'react-native';
import { useAdaptAndGroupVariantPacks } from '@/hooks/use-adapt-and-group-item-list-options';
import { useItemDetailContext } from '@/contexts/item-detail-context';
import { useNewOrderActionsContext } from '@/contexts/new-order-actions-context';
import { VolumePromotion } from '@/utils/volume-promotions-types';
import { VolumePromotionBanner } from './volume-promotion-banner';

interface Props {
  promotion: VolumePromotion;
  source: string;
  style?: StyleProp<ViewStyle>;
}

export const VolumePromotionListItemOrderableItem: React.FC<Props> = ({
  promotion,
  source,
  style,
}) => {
  const adaptAndGroupVariantPacks = useAdaptAndGroupVariantPacks();
  const { debouncedUpdateQuantity } = useNewOrderActionsContext();
  const { selectItemListOption } = useItemDetailContext();
  const [styles] = useStyles();

  const option = useMemo(() => {
    if (promotion.promotedVariantPacks.length === 1) {
      const firstVariantPack = promotion.promotedVariantPacks[0].variantPack;
      return adaptAndGroupVariantPacks([firstVariantPack], promotion.uuid)[0];
    } else {
      return null;
    }
  }, [adaptAndGroupVariantPacks, promotion]);

  if (!option) {
    return null;
  }

  const hasImageBackground =
    promotion.backgroundImageUrl && promotion.backgroundImageUrl.trim() !== '';

  return (
    <EqualHeightTouchableOpacity
      delayPressIn={SCROLL_DELAY}
      onPress={() => {
        if (option) {
          selectItemListOption(option, {
            source: `${source}_promotions_list`,
          });
        }
      }}
      style={[
        styles.base,
        hasImageBackground ? styles.baseWithImage : null,
        style,
      ]}
    >
      <View style={styles.content}>
        <VolumePromotionBanner
          marketingTextStyle={TYPOGRAPHY.pSemiBold}
          promotion={promotion}
          style={styles.banner}
        />
        {hasImageBackground ? null : (
          <OrderableItemListItemWithRowQuantity
            containerStyle={styles.listItemContainer}
            contentStyle={styles.listItemContent}
            isFocused={false}
            option={option}
            source={`${source}_promotions_carousel`}
            updateQuantity={(updateQuantityData) => {
              debouncedUpdateQuantity(updateQuantityData, {
                source: `${source}_promotions_carousel`,
              });
            }}
            visibleLabels={[]}
          />
        )}
      </View>
    </EqualHeightTouchableOpacity>
  );
};

const useStyles = createStyles((theme) => ({
  banner: {
    borderTopLeftRadius: BORDER_RADIUS_SMALL,
    borderTopRightRadius: BORDER_RADIUS_SMALL,
    flex: -1,
    minHeight: 50,
  },
  base: {
    borderRadius: BORDER_RADIUS_SMALL,
    paddingBottom: 8,
  },
  baseWithImage: {
    height: 280,
  },
  content: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: BORDER_RADIUS_SMALL,
    borderWidth: 1,
    flex: 1,
    overflow: 'hidden',
  },
  listItemContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderBottomLeftRadius: BORDER_RADIUS_SMALL,
    borderBottomRightRadius: BORDER_RADIUS_SMALL,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    paddingHorizontal: 0,
  },
  listItemContent: {
    flex: 1,
  },
}));
